/**
 * Shared utility for generating unique entity IDs with profanity filtering
 */

// Common inappropriate words/letter combinations to filter out
const BLOCKED_WORDS = [
  'ASS', 'FAG', 'FUK', 'FUC', 'FCK', 'SEX', 'DIK', 'DIE', 'KKK',
  'GAY', 'WTF', 'DMN', 'SHT', 'CUM', 'COK', 'VAG', 'TIT',
  'PIS', 'POO', 'PUS', 'NOB', 'KYS', 'RIP', 'WAR', 'DIE',
  'KIL', 'MUR', 'RAP', 'HOE', 'SLT', 'WHR', 'FAT', 'UGY',
  'DUM', 'BTH', 'CNT', 'TWT', 'BAD', 'EVL', 'SIN', 'HEL', 'JEW',
  'XXX'
];

/**
 * Generates a random 3-letter string from A-Z
 */
function generateRandomLetters(length: number = 3): string {
  return Array.from({ length }, () =>
    String.fromCharCode(65 + Math.floor(Math.random() * 26))
  ).join('');
}

/**
 * Generates a random numeric string
 */
function generateRandomNumbers(length: number = 5): string {
  return Array.from({ length }, () =>
    Math.floor(Math.random() * 10)
  ).join('');
}

/**
 * Checks if a string contains any blocked words
 */
function containsBlockedWord(str: string): boolean {
  const upperStr = str.toUpperCase();
  return BLOCKED_WORDS.some(word => upperStr.includes(word));
}

/**
 * Generates a safe random letter combination (no profanity)
 * Will retry up to maxAttempts times if blocked words are found
 */
function generateSafeLetters(length: number = 3, maxAttempts: number = 100): string {
  for (let i = 0; i < maxAttempts; i++) {
    const letters = generateRandomLetters(length);
    if (!containsBlockedWord(letters)) {
      return letters;
    }
  }

  // Fallback: if we can't find a safe combination after maxAttempts,
  // use a guaranteed safe pattern (alternating consonants and vowels)
  const safeConsonants = ['B', 'D', 'F', 'G', 'H', 'J', 'L', 'M', 'N', 'P', 'R', 'S', 'T', 'V', 'W', 'Y', 'Z'];
  const safeVowels = ['A', 'E', 'I', 'O', 'U'];

  let result = '';
  for (let i = 0; i < length; i++) {
    if (i % 2 === 0) {
      result += safeConsonants[Math.floor(Math.random() * safeConsonants.length)];
    } else {
      result += safeVowels[Math.floor(Math.random() * safeVowels.length)];
    }
  }

  return result;
}

/**
 * Generates a unique entity ID with format: [PREFIX-]LLLNNNNN
 * - PREFIX: Optional prefix like 'BAA-' or 'COI-'
 * - LLL: 3 random letters (filtered for profanity)
 * - NNNNN: 5 random numbers
 *
 * @param prefix Optional prefix to add (e.g., 'BAA-', 'COI-')
 * @returns Unique ID string
 *
 * @example
 * generateEntityId() // Returns: "ABC12345"
 * generateEntityId('BAA-') // Returns: "BAA-DEF67890"
 * generateEntityId('COI-') // Returns: "COI-GHI11111"
 */
export function generateEntityId(prefix: string = ''): string {
  const letters = generateSafeLetters(3);
  const numbers = generateRandomNumbers(5);

  return `${prefix}${letters}${numbers}`;
}

/**
 * Convenience function for generating Contract IDs
 */
export function generateContractId(): string {
  return generateEntityId();
}

/**
 * Convenience function for generating BAA IDs
 */
export function generateBaaId(): string {
  return generateEntityId('BAA-');
}

/**
 * Convenience function for generating COI IDs
 */
export function generateCoiId(): string {
  return generateEntityId('COI-');
}
